import {HttpClient, HttpErrorResponse} from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import {Store} from "@ngrx/store";
import {lastValueFrom, take} from "rxjs";
import {ASSET_MANAGEMENT_URL} from "../../../app.config";
import {API_ENDPOINTS, APP_CONSTANTS} from "../../../app.constants";
import {ToastSeverityEnum} from "../../../enums";
import {prepareFiltersForParams} from "../../../helpers";
import {DtoCustomerOrgInterface, DtoCustomerResponseResultInterface} from "../../../interfaces";
import {mapCustomerFromDtoArray} from "../../../mapping";
import {
  setCustomersPaginationInfo,
  setPaginatedCustomers
} from "../../../store/actions/customers.actions";
import {selectCustomerFilters} from "../../../store/reducers/customers.reducer";
import {ToastsService} from "../../app-services";

@Injectable({
  providedIn: "root"
})
export class CustomersService {
  private readonly httpClient = inject(HttpClient);
  private readonly store = inject(Store);
  private readonly toastsService = inject(ToastsService);
  private readonly assetManagementUrl = inject(ASSET_MANAGEMENT_URL);

  constructor() {
    this.store.select(selectCustomerFilters).subscribe(() => {
      this.getPaginatedCustomers();
    });
  }

  public getPaginatedCustomers() {
    this.store
      .select(selectCustomerFilters)
      .pipe(take(1))
      .subscribe(filters => {
        const queryParams = prepareFiltersForParams(filters);
        this.httpClient
          .get<DtoCustomerResponseResultInterface>(this.assetManagementUrl + API_ENDPOINTS.CUSTOMERS, {params: queryParams})
          .subscribe({
            next: data => {
              this.store.dispatch(setPaginatedCustomers({customers: mapCustomerFromDtoArray(data.results ?? [])}));
              this.store.dispatch(
                setCustomersPaginationInfo({
                  paginationInfo: {
                    from: (data.offset ?? 0) + 1,
                    to: (data.offset ?? 0) + (data.count ?? 0),
                    totalResults: data.totalCount ?? 0,
                    currentPage: (data.offset ?? 0) / APP_CONSTANTS.PAGINATION_PAGE_SIZE + 1,
                    totalPages: Math.ceil((data.totalCount ?? 0) / APP_CONSTANTS.PAGINATION_PAGE_SIZE)
                  }
                })
              );
            },
            error: (error: HttpErrorResponse) => {
              this.toastsService.addToast({
                severity: ToastSeverityEnum.Error,
                message: "Error fetching Customers Paginated, Status " + error.status
              });
            }
          });
      });
  }

  public createCustomerTrumpfOrgMapping(customerNumber: string, shortCode: string): void {
    const customerNumberEncoded = encodeURIComponent(customerNumber);
    const shortCodeEncoded = encodeURIComponent(shortCode);
    this.httpClient
      .put(this.assetManagementUrl + API_ENDPOINTS.CUSTOMERS + `/${customerNumberEncoded}/TrumpfCompanies/${shortCodeEncoded}`, {})
      .subscribe({
        next: () => {
          this.getPaginatedCustomers();
          this.toastsService.addToast({
            severity: ToastSeverityEnum.Success,
            message: `Create new Mapping for Customer ${customerNumber} and Trumpf Company ${shortCode}`
          });
        },
        error: (error: HttpErrorResponse) => {
          this.toastsService.addToast({
            severity: ToastSeverityEnum.Error,
            message: `Failed to create new Mapping for Customer ${customerNumber}
          and Trumpf Company ${shortCode}, Status: ${error.status}`
          });
        }
      });
  }

  public deleteCustomerTrumpfOrgMapping(customerNumber: string, shortCode: string): void {
    const customerNumberEncoded = encodeURIComponent(customerNumber);
    const shortCodeEncoded = encodeURIComponent(shortCode);
    this.httpClient
      .delete(this.assetManagementUrl + API_ENDPOINTS.CUSTOMERS + `/${customerNumberEncoded}/TrumpfCompanies/${shortCodeEncoded}`)
      .subscribe({
        next: () => {
          this.getPaginatedCustomers();
          this.toastsService.addToast({
            severity: ToastSeverityEnum.Success,
            message: `Deleted Mapping for Customer ${customerNumber} and Trumpf Company ${shortCode}`
          });
        },
        error: (error: HttpErrorResponse) => {
          this.toastsService.addToast({
            severity: ToastSeverityEnum.Error,
            message: `Failed to delete Mapping for Customer ${customerNumber}
          and Trumpf Company ${shortCode}, Status: ${error.status}`
          });
        }
      });
  }

  public async checkCustomerNumber(customerNumber: string): Promise<boolean> {
    const customerNumberEncoded = encodeURIComponent(customerNumber);
    const customer = await lastValueFrom(
      this.httpClient.get<DtoCustomerOrgInterface>(this.assetManagementUrl + API_ENDPOINTS.CUSTOMERS + `/${customerNumberEncoded}`)
    );
    return customer.customerOrgId !== undefined && customer.customerOrgId !== "";
  }
}
